import { api } from './api';
import { ApiResponse } from '../types';

export interface DashboardStats {
  totalPatients: number;
  todayAppointments: number;
  pendingInvoices: number;
  lowStockItems: number;
  activeUsers: number;
  monthlyRevenue: number;
  newPatientsThisMonth: number;
  completedAppointmentsThisMonth: number;
}

export interface AppointmentChartData {
  date: string;
  appointments: number;
  completed: number;
}

export interface RevenueChartData {
  month: string;
  revenue: number;
  appointments: number;
}

export interface SpeciesData {
  species: string;
  count: number;
  percentage: number;
}

export interface RecentActivity {
  id: string;
  type: 'appointment' | 'invoice' | 'patient' | 'medical_record' | 'inventory';
  title: string;
  description?: string;
  timestamp: string;
  icon?: string;
  time?: string;
  userId?: string;
  user?: {
    name: string;
  };
}

class DashboardService {
  // Get dashboard statistics
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    return api.get('/dashboard/stats');
  }

  // Get appointment data for charts
  async getAppointmentChart(period: 'week' | 'month' | 'year' = 'month'): Promise<ApiResponse<AppointmentChartData[]>> {
    return api.get(`/dashboard/appointments-chart?period=${period}`);
  }

  // Get revenue data for charts
  async getRevenueChart(period: 'week' | 'month' | 'year' = 'month'): Promise<ApiResponse<RevenueChartData[]>> {
    return api.get(`/dashboard/revenue-chart?period=${period}`);
  }

  // Get species distribution
  async getSpeciesDistribution(): Promise<ApiResponse<SpeciesData[]>> {
    return api.get('/dashboard/species-distribution');
  }

  // Get recent activity
  async getRecentActivity(limit: number = 10): Promise<ApiResponse<RecentActivity[]>> {
    return api.get(`/dashboard/recent-activity?limit=${limit}`);
  }

  // Get top services
  async getTopServices(limit: number = 5): Promise<ApiResponse<Array<{ name: string; count: number; revenue: number }>>> {
    return api.get(`/dashboard/top-services?limit=${limit}`);
  }

  // Get upcoming appointments
  async getUpcomingAppointments(limit: number = 5): Promise<ApiResponse<Array<{
    id: string;
    patientName: string;
    appointmentDate: string;
    appointmentTime: string;
    type: string;
    veterinarian?: string;
  }>>> {
    return api.get(`/dashboard/upcoming-appointments?limit=${limit}`);
  }

  // Get low stock items
  async getLowStockItems(limit: number = 10): Promise<ApiResponse<Array<{
    id: string;
    name: string;
    stockQuantity: number;
    minStockAlert: number;
    category: string;
  }>>> {
    return api.get(`/dashboard/low-stock?limit=${limit}`);
  }
}

export const dashboardService = new DashboardService();